import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Typography,
  List,
  Avatar,
  Input,
  Button,
  Divider,
  Dropdown,
  Row,
  Col,
  Empty,
  Spin,
  message,
  Tooltip,
} from "antd";
import {
  SendOutlined,
  DownOutlined,
  CommentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  EditOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import preguntasObservaciones from "./preguntasObservacionesDialog";
import observacionesEditables from "./observacionesEditablesDialog";
import "./ObservacionesDialog.css";

const { Title, Text } = Typography;
const { TextArea, Search } = Input;

function ObservacionesDialog({ open, onClose, trasladoId }) {
  const { user } = useUser();
  const [observaciones, setObservaciones] = useState([]);
  const [nuevaObservacion, setNuevaObservacion] = useState("");
  const [filtroPregunta, setFiltroPregunta] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const listEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (trasladoId && open) {
      setLoading(true);
      api
        .get(`/observaciones/${trasladoId}`)
        .then((response) => {
          setObservaciones(response.data);
          setTimeout(scrollToBottom, 100);
        })
        .catch((error) => {
          console.error("Error al obtener las observaciones:", error);
          message.error("Error al cargar las observaciones");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [trasladoId, open]);

  const handleAgregarObservacion = async (texto = nuevaObservacion) => {
    if (!texto.trim() || !user) return;

    setSending(true);
    try {
      const response = await api.post("/observaciones", {
        traslado_id: trasladoId,
        usuario_id: user.id,
        texto_observacion: texto,
      });

      const observacion = response.data.observacion || response.data;
      const nuevaObservacionConUsuario = {
        ...observacion,
        Usuario: { nombre: user.nombre },
      };

      setObservaciones([...observaciones, nuevaObservacionConUsuario]);
      setNuevaObservacion("");
      message.success("Observación agregada correctamente");
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Error al agregar la observación:", error);
      message.error("Error al agregar la observación");
    } finally {
      setSending(false);
    }
  };

  const handleMenuClick = (key) => {
    if (observacionesEditables.includes(key)) {
      setNuevaObservacion(key);
      setDropdownOpen(false); // Cerrar el dropdown para editables
    } else {
      handleAgregarObservacion(key);
      setDropdownOpen(false); // Cerrar el dropdown para las directas también
    }
  };

  const preguntasFiltradas = preguntasObservaciones.filter((pregunta) =>
    pregunta.toLowerCase().includes(filtroPregunta.toLowerCase())
  );

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diferencia = ahora - date;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);

    if (minutos < 1) return "Hace un momento";
    if (minutos < 60) return `Hace ${minutos} min`;
    if (horas < 24) return `Hace ${horas}h`;
    if (dias < 7) return `Hace ${dias}d`;
    return date.toLocaleDateString("es-ES", { 
      day: "numeric", 
      month: "short", 
      year: "numeric" 
    });
  };

  const getAvatarColor = (nombre) => {
    const colors = [
      "#1890ff", "#52c41a", "#faad14", "#f5222d", 
      "#722ed1", "#13c2c2", "#eb2f96", "#fa8c16"
    ];
    let hash = 0;
    for (let i = 0; i < nombre.length; i++) {
      hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="observaciones-modal-header">
          <div className="header-icon">
            <CommentOutlined />
          </div>
          <div className="header-content">
            <Title level={4} className="header-title">
              Observaciones de Seguimiento
            </Title>
            <Text type="secondary" className="header-subtitle">
              {observaciones.length} observaciones • Traslado #{trasladoId}
            </Text>
          </div>
        </div>
      }
      width={800}
      centered
      destroyOnHidden
      className="observaciones-modal"
      styles={{
        body: { padding: 0 },
        header: { padding: '24px 24px 0 24px', borderBottom: 'none' }
      }}
    >
      <div className="observaciones-container">
        {/* Lista de observaciones */}
        <div 
          className="observaciones-list-container"
          ref={scrollContainerRef}
        >
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
              <Text type="secondary" style={{ marginTop: 16 }}>
                Cargando observaciones...
              </Text>
            </div>
          ) : observaciones.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text type="secondary">No hay observaciones aún</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Sé el primero en agregar una observación
                  </Text>
                </div>
              }
            />
          ) : (
            <List
              dataSource={observaciones}
              renderItem={(observacion, index) => {
                const isOwn = observacion.Usuario && observacion.Usuario.nombre === user.nombre;
                const isFirst = index === 0;
                const isLast = index === observaciones.length - 1;
                
                return (
                  <div 
                    key={observacion.id} 
                    className={`observacion-item ${isOwn ? 'own' : 'other'} ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''}`}
                  >
                    <div className="observacion-content">
                      <div className="observacion-avatar">
                        <Avatar 
                          style={{ 
                            backgroundColor: getAvatarColor(observacion.Usuario?.nombre || "Usuario"),
                            border: '2px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          {observacion.Usuario?.nombre?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                      </div>
                      
                      <div className="observacion-bubble">
                        <div className="observacion-header">
                          <Text strong className="observacion-author">
                            {observacion.Usuario?.nombre || "Usuario desconocido"}
                          </Text>
                          <Tooltip title={new Date(observacion.created_at).toLocaleString()}>
                            <Text type="secondary" className="observacion-time">
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              {formatearFecha(observacion.created_at)}
                            </Text>
                          </Tooltip>
                        </div>
                        
                        <div className="observacion-message">
                          <Text>{observacion.texto_observacion}</Text>
                        </div>
                        
                        <div className="observacion-status">
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Enviado
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
          <div ref={listEndRef} />
        </div>

        {/* Área de entrada */}
        <div className="observaciones-input-container">
          <Divider style={{ margin: '16px 0' }} />
          
          <div className="input-section">
            <div className="textarea-container">
              <TextArea
                rows={3}
                placeholder="Escribe tu observación..."
                value={nuevaObservacion}
                onChange={(e) => setNuevaObservacion(e.target.value)}
                className="observacion-textarea"
                maxLength={1000}
                showCount
                disabled={sending}
              />
            </div>
            
            <div className="actions-container">
              <Row gutter={12}>
                <Col span={14}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => handleAgregarObservacion()}
                    disabled={!nuevaObservacion.trim() || sending}
                    loading={sending}
                    block
                    size="large"
                    className="send-button"
                  >
                    {sending ? 'Enviando...' : 'Enviar Observación'}
                  </Button>
                </Col>
                <Col span={10}>
                  <Dropdown
                    trigger={["click"]}
                    disabled={sending}
                    open={dropdownOpen}
                    onOpenChange={setDropdownOpen}
                    dropdownRender={() => (
                      <div className="quick-dropdown">
                        <div className="dropdown-header">
                          <div className="dropdown-header-icon">
                            <SearchOutlined />
                          </div>
                          <div className="dropdown-header-content">
                            <Text strong className="dropdown-header-title">Observaciones rápidas</Text>
                          </div>
                        </div>
                        <div className="dropdown-search-container">
                          <Search
                            placeholder="Buscar observación..."
                            allowClear
                            onChange={(e) => setFiltroPregunta(e.target.value)}
                            className="dropdown-search"
                            size="small"
                          />
                        </div>
                        <div className="dropdown-menu">
                          {preguntasFiltradas.length === 0 ? (
                            <div className="dropdown-empty">
                              <Empty 
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No se encontraron observaciones"
                                style={{ margin: '16px 0' }}
                              />
                            </div>
                          ) : (
                            preguntasFiltradas.map((pregunta, index) => {
                              const isEditable = observacionesEditables.includes(pregunta);
                              return (
                                <div
                                  key={pregunta}
                                  className={`dropdown-item ${isEditable ? 'editable' : 'direct'}`}
                                  onClick={() => handleMenuClick(pregunta)}
                                >
                                  <div className="dropdown-item-icon">
                                    {isEditable ? (
                                      <EditOutlined style={{ color: '#faad14' }} />
                                    ) : (
                                      <SendOutlined style={{ color: '#52c41a' }} />
                                    )}
                                  </div>
                                  <div className="dropdown-item-content">
                                    <Text className="dropdown-item-text">
                                      {pregunta}
                                    </Text>
                                    <Text type="secondary" className="dropdown-item-hint">
                                      {isEditable ? 'Clic para editar' : 'Clic para enviar'}
                                    </Text>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div className="dropdown-footer">
                          <Text type="secondary" className="dropdown-footer-text">
                            💡 Las observaciones marcadas como "Editable" se pueden modificar antes de enviar
                          </Text>
                        </div>
                      </div>
                    )}
                  >
                    <Button 
                      icon={<DownOutlined />} 
                      block 
                      size="large"
                      className="quick-button"
                    >
                      Insertar Rápida
                    </Button>
                  </Dropdown>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

ObservacionesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default ObservacionesDialog;